/**
 * Service de synchronisation en arrière-plan
 * Identique à Flutter SyncService
 */

import gameStateService from './gameStateService';
import getSupabaseClient from './supabaseService';

class SyncService {
  private syncInterval: NodeJS.Timeout | null = null;
  private isSyncing = false;
  private lastSyncTime: Date | null = null;
  private syncError: string | null = null;
  private authListener: (() => void) | null = null;

  /**
   * Initialise le service de synchronisation
   * Doit être appelé au démarrage de l'application
   */
  init(): void {
    console.log('[SyncService] Initializing sync service');
    
    // Vérifier l'état initial de connexion
    this.checkAuthAndStartSync();
    
    // Écouter les changements d'authentification
    const supabase = getSupabaseClient();
    const {data: {subscription}} = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`[SyncService] Auth state changed: ${event}`);
      if (session) {
        this.startSync();
      } else {
        this.stopSync();
      }
    });
    
    this.authListener = () => {
      subscription.unsubscribe();
    };
  }

  /**
   * Vérifie l'authentification et démarre la synchronisation si nécessaire
   */
  private async checkAuthAndStartSync(): Promise<void> {
    try {
      const supabase = getSupabaseClient();
      const {data: {session}} = await supabase.auth.getSession();
      if (session?.user) {
        this.startSync();
      }
    } catch (error) {
      console.error('[SyncService] Error checking auth:', error);
    }
  }

  /**
   * Démarre la synchronisation périodique
   */
  private startSync(): void {
    // Annuler le timer existant s'il y en a un
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    // Synchroniser au démarrage
    this.sync();
    
    // Synchroniser toutes les 5 minutes (comme Flutter)
    this.syncInterval = setInterval(() => {
      this.sync();
    }, 5 * 60 * 1000); // 5 minutes
    
    console.log('[SyncService] Sync started (every 5 minutes)');
  }

  /**
   * Arrête la synchronisation périodique
   */
  private stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    console.log('[SyncService] Sync stopped');
  }

  /**
   * Synchronise les gameStates avec le serveur
   */
  async sync(): Promise<void> {
    if (this.isSyncing) {
      console.log('[SyncService] Sync already in progress, skipping...');
      return;
    }
    
    try {
      const supabase = getSupabaseClient();
      const {data: {session}} = await supabase.auth.getSession();
      
      if (!session?.user) {
        console.log('[SyncService] User not logged in, skipping sync...');
        return;
      }
    } catch (error) {
      console.error('[SyncService] Error checking auth:', error);
      return;
    }

    try {
      this.isSyncing = true;
      this.syncError = null;

      console.log('[SyncService] Starting sync...');
      await gameStateService.syncAll();
      console.log('[SyncService] Sync completed successfully');

      this.lastSyncTime = new Date();
    } catch (e: any) {
      console.error('[SyncService] Sync failed:', e);
      this.syncError = e.toString();
      // Ne pas rethrow pour éviter de bloquer l'application
      // L'utilisateur pourra réessayer manuellement via le widget de statut
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Force une synchronisation immédiate
   */
  async forceSync(): Promise<void> {
    try {
      const supabase = getSupabaseClient();
      const {data: {session}} = await supabase.auth.getSession();
      
      if (!session?.user) {
        throw new Error('User not authenticated');
      }
    } catch (error) {
      throw new Error('User not authenticated');
    }
    
    await this.sync();
  }

  /**
   * Synchronise avant de quitter l'application
   */
  async syncBeforeExit(): Promise<void> {
    try {
      const supabase = getSupabaseClient();
      const {data: {session}} = await supabase.auth.getSession();
      
      if (session?.user) {
        await this.sync();
      }
    } catch (error) {
      console.error('[SyncService] Error during final sync:', error);
    } finally {
      this.stopSync();
      if (this.authListener) {
        this.authListener();
      }
    }
  }

  /**
   * Getters pour l'état de synchronisation
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  getLastSyncTime(): Date | null {
    return this.lastSyncTime;
  }

  getSyncError(): string | null {
    return this.syncError;
  }
}

export default new SyncService();
